<template>
    <div class="content-wrapper location-crud">
        <div class="row align-items-center">
            <div class="col-sm-12 col-md-6">
                <app-breadcrumb :page-title="$t('locations')" :icon="'map-pin'" />
            </div>
            <div class="col-sm-12 col-md-6 breadcrumb-side-button">
                <div class="float-md-right mb-3 mb-sm-3 mb-md-0 location-crud__header-actions">
                    <button
                        type="button"
                        class="btn btn-primary btn-with-shadow"
                        :class="{ active: activePanel === 'add' }"
                        :disabled="!canCreate"
                        @click="activateAddMode"
                    >
                        Ajouter
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-primary"
                        :class="{ active: activePanel === 'search' }"
                        @click="activateSearchMode"
                    >
                        Rechercher
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-primary"
                        :class="{ active: activePanel === 'configure' }"
                        :disabled="!locations.length"
                        @click="activateConfigureMode"
                    >
                        Configurer
                    </button>
                    <button
                        type="button"
                        class="btn btn-outline-primary"
                        :class="{ active: pickOnMapEnabled }"
                        :disabled="!hasEditorAccess"
                        @click="togglePickOnMap"
                    >
                        {{ pickOnMapEnabled ? 'Positionnement actif' : 'Positionner carte' }}
                    </button>
                </div>
            </div>
        </div>

        <div class="location-crud__workspace">
            <aside class="location-crud__sidebar">
                <section
                    class="location-crud__panel location-crud__panel--search"
                    :class="{ 'location-crud__panel--active': activePanel === 'search' }"
                >
                    <div class="location-crud__panel-head">
                        <div>
                            <small>Navigation</small>
                            <h4>Retrouver un lieu</h4>
                            <p>Recherchez, recentrez la carte, puis éditez depuis le panneau sans ouvrir de modal.</p>
                        </div>
                        <span class="location-crud__count">{{ filteredLocations.length }}/{{ catalogTotal }}</span>
                    </div>

                    <div class="location-crud__search">
                        <div class="input-group">
                            <input
                                ref="searchInput"
                                v-model.trim="searchQuery"
                                type="text"
                                class="form-control"
                                placeholder="Nom, type, code ou coordonnées"
                                @focus="activePanel = 'search'"
                                @keyup.enter="openFirstSearchResult"
                            />
                            <div class="input-group-append">
                                <button type="button" class="btn btn-outline-secondary" @click="openFirstSearchResult">
                                    Ouvrir
                                </button>
                                <button type="button" class="btn btn-outline-secondary" @click="clearSearch">
                                    Effacer
                                </button>
                            </div>
                        </div>
                    </div>

                    <div class="location-crud__stats">
                        <article class="location-crud__stat-card">
                            <span class="location-crud__stat-label">Lieux</span>
                            <strong>{{ catalogTotal }}</strong>
                        </article>
                        <article class="location-crud__stat-card">
                            <span class="location-crud__stat-label">Incidents</span>
                            <strong>{{ incidents.length }}</strong>
                        </article>
                    </div>

                    <div class="location-crud__assistant-note">
                        <strong>{{ workspaceAssistantTitle }}</strong>
                        <span>{{ workspaceAssistantMessage }}</span>
                    </div>

                    <div class="location-crud__toggles">
                        <label class="location-crud__toggle">
                            <input v-model="showIncidents" type="checkbox" />
                            <span>Afficher les incidents</span>
                        </label>
                        <label class="location-crud__toggle">
                            <input v-model="showRoads" type="checkbox" />
                            <span>Afficher les routes prioritaires</span>
                        </label>
                    </div>
                </section>

                <section
                    class="location-crud__panel location-crud__panel--list"
                    :class="{ 'location-crud__panel--active': activePanel === 'search' || activePanel === 'configure' }"
                >
                    <div class="location-crud__panel-head">
                        <div>
                            <small>Catalogue</small>
                            <h4>Lieux disponibles</h4>
                        </div>
                        <button
                            type="button"
                            class="btn btn-sm btn-outline-secondary"
                            @click="fitMapToVisibleLocations"
                        >
                            Vue d'ensemble
                        </button>
                    </div>

                    <div v-if="isLoadingLocations" class="location-crud__empty">
                        Chargement des lieux...
                    </div>

                    <div v-else-if="filteredLocations.length" ref="catalogList" class="location-crud__list">
                        <article
                            v-for="location in filteredLocations"
                            :key="location.id"
                            class="location-crud__list-item"
                            :class="{ active: Number(selectedLocationId) === Number(location.id) }"
                        >
                            <button
                                type="button"
                                class="location-crud__list-card"
                                @click="selectLocation(location, { center: true, openInfo: true })"
                            >
                                <div class="location-crud__list-main">
                                    <div class="location-crud__list-title">{{ getCatalogLocationName(location) }}</div>
                                    <small class="location-crud__list-subtitle">{{ getCatalogLocationTypeName(location) }}</small>
                                </div>
                            </button>

                            <!-- <div class="location-crud__list-row-actions">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary"
                                    @click="focusLocation(location)"
                                >
                                    Centrer
                                </button>
                                <button
                                    type="button"
                                    class="btn btn-sm btn-primary"
                                    @click="configureLocation(location)"
                                >
                                    Configurer
                                </button>
                            </div> -->
                        </article>
                    </div>

                    <div v-else class="location-crud__empty">
                        Aucun lieu ne correspond à votre recherche.
                    </div>

                    <div v-if="showCatalogPagination" class="location-crud__pagination">
                        <div class="location-crud__pagination-summary">
                            <strong>{{ locationPagination.from }}-{{ locationPagination.to }}</strong>
                            <span>sur {{ catalogTotal }}</span>
                        </div>

                        <div class="location-crud__pagination-controls">
                            <label class="location-crud__per-page">
                                <span>Par page</span>
                                <select
                                    v-model.number="locationPagination.perPage"
                                    class="form-control form-control-sm"
                                    :disabled="isLoadingLocations"
                                    @change="changeLocationPerPage"
                                >
                                    <option
                                        v-for="option in locationPerPageOptions"
                                        :key="option"
                                        :value="option"
                                    >
                                        {{ option }}
                                    </option>
                                </select>
                            </label>

                            <div class="location-crud__pagination-nav">
                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary location-crud__pagination-icon"
                                    :disabled="isLoadingLocations || locationPagination.currentPage <= 1"
                                    aria-label="Page précédente"
                                    title="Page précédente"
                                    @click="changeLocationPage(locationPagination.currentPage - 1)"
                                >
                                    <app-icon :name="'chevron-left'" class="size-16" />
                                </button>

                                <span class="location-crud__pagination-status">
                                    {{ locationPagination.currentPage }} / {{ locationPagination.lastPage }}
                                </span>

                                <button
                                    type="button"
                                    class="btn btn-sm btn-outline-secondary location-crud__pagination-icon"
                                    :disabled="isLoadingLocations || locationPagination.currentPage >= locationPagination.lastPage"
                                    aria-label="Page suivante"
                                    title="Page suivante"
                                    @click="changeLocationPage(locationPagination.currentPage + 1)"
                                >
                                    <app-icon :name="'chevron-right'" class="size-16" />
                                </button>
                            </div>
                        </div>
                    </div>
                </section>

            </aside>

            <section class="location-crud__map-section" :class="{ 'location-crud__map-section--drawer-open': isEditorDrawerOpen }">
                <div class="location-crud__map-toolbar">
                    <div class="location-crud__legend">
                        <span v-if="selectedLocation" class="location-crud__chip location-crud__chip--filled">
                            Édition de {{ selectedLocation.name }}
                        </span>
                        <span v-if="pickOnMapEnabled" class="location-crud__chip">
                            Cliquez sur la carte pour définir les coordonnées
                        </span>
                        <span v-else-if="hasCoordinates" class="location-crud__chip">
                            Aperçu cartographique actif
                        </span>
                    </div>

                    <div class="location-crud__map-actions">
                        <button type="button" class="btn btn-light btn-sm" @click="fitMapToVisibleLocations">
                            Vue d'ensemble
                        </button>
                        <button
                            v-if="selectedLocation || hasCoordinates"
                            type="button"
                            class="btn btn-light btn-sm"
                            @click="centerMapOnActiveTarget"
                        >
                            Centrer
                        </button>
                    </div>
                </div>

                <div v-if="pickOnMapEnabled" class="location-crud__map-notice">
                    Positionnement actif : un clic sur la carte place ou déplace le point à enregistrer.
                </div>

                <div class="location-crud__map-canvas">
                    <div id="map" ref="map"></div>

                    <transition name="location-editor-drawer">
                        <aside
                            v-if="isEditorDrawerOpen"
                            class="location-crud__drawer"
                            :class="{ 'location-crud__drawer--active': activePanel === 'add' || activePanel === 'configure' }"
                        >
                            <div class="location-crud__drawer-header">
                                <div>
                                    <small>{{ selectedLocation ? 'Édition' : 'Création hybride' }}</small>
                                    <h4>{{ selectedLocation ? selectedLocation.name : 'Configurer un lieu' }}</h4>
                                    <p>{{ editorDescription }}</p>
                                </div>
                                <div class="location-crud__drawer-header-actions">
                                    <button
                                        v-if="hasEditorAccess"
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        @click="resetEditor"
                                    >
                                        Réinitialiser
                                    </button>
                                    <button
                                        type="button"
                                        class="close outline-none"
                                        @click.prevent="closeEditorDrawer"
                                    >
                                        <span>
                                            <app-icon :name="'x'"></app-icon>
                                        </span>
                                    </button>
                                </div>
                            </div>

                            <div
                                class="location-crud__hint"
                                :class="{ 'location-crud__hint--active': pickOnMapEnabled }"
                            >
                                {{ positioningHint }}
                            </div>

                            <form class="location-crud__form location-crud__drawer-form" @submit.prevent="saveLocation">
                                <div class="form-group">
                                    <label for="location-name">
                                        {{ $t('name') }}
                                        <span class="text-danger">*</span>
                                    </label>
                                    <input
                                        id="location-name"
                                        ref="nameInput"
                                        v-model.trim="form.name"
                                        type="text"
                                        class="form-control"
                                        :disabled="!canEditCurrentForm"
                                        placeholder="Ex. Check Point Kasumbalesa"
                                    />
                                </div>

                                <div class="location-crud__grid">
                                    <div class="form-group">
                                        <label for="location-code">Code</label>
                                        <input
                                            id="location-code"
                                            v-model.trim="form.code"
                                            type="text"
                                            class="form-control"
                                            :disabled="!canEditCurrentForm"
                                            placeholder="Code interne"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="location-type">
                                            {{ $t('location_type') }}
                                            <span class="text-danger">*</span>
                                        </label>
                                        <select
                                            id="location-type"
                                            v-model.number="form.location_type_id"
                                            class="form-control"
                                            :disabled="!canEditCurrentForm"
                                        >
                                            <option :value="null">Choisir un type</option>
                                            <option
                                                v-for="type in locationTypeList"
                                                :key="type.id"
                                                :value="type.id"
                                            >
                                                {{ type.name }}
                                            </option>
                                        </select>
                                    </div>
                                </div>

                                <div class="form-group">
                                    <label for="location-description">{{ $t('description') }}</label>
                                    <textarea
                                        id="location-description"
                                        v-model.trim="form.description"
                                        class="form-control"
                                        rows="2"
                                        :disabled="!canEditCurrentForm"
                                        placeholder="Description visible pour les équipes terrain"
                                    ></textarea>
                                </div>

                                <div class="location-crud__grid">
                                    <div class="form-group">
                                        <label for="location-address">Adresse / repère</label>
                                        <input
                                            id="location-address"
                                            v-model.trim="form.address"
                                            type="text"
                                            class="form-control"
                                            :disabled="!canEditCurrentForm"
                                            placeholder="Repère, axe routier, village..."
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="location-comment">Commentaire</label>
                                        <input
                                            id="location-comment"
                                            v-model.trim="form.comment"
                                            type="text"
                                            class="form-control"
                                            :disabled="!canEditCurrentForm"
                                            placeholder="Notes opérationnelles"
                                        />
                                    </div>
                                </div>

                                <div class="location-crud__grid">
                                    <div class="form-group">
                                        <label for="location-latitude">
                                            Latitude
                                            <span class="text-danger">*</span>
                                        </label>
                                        <input
                                            id="location-latitude"
                                            v-model.trim="form.lat"
                                            type="text"
                                            inputmode="decimal"
                                            class="form-control"
                                            :disabled="!canEditCurrentForm"
                                            placeholder="-11.660900"
                                        />
                                    </div>

                                    <div class="form-group">
                                        <label for="location-longitude">
                                            Longitude
                                            <span class="text-danger">*</span>
                                        </label>
                                        <input
                                            id="location-longitude"
                                            v-model.trim="form.lng"
                                            type="text"
                                            inputmode="decimal"
                                            class="form-control"
                                            :disabled="!canEditCurrentForm"
                                            placeholder="27.479400"
                                        />
                                    </div>
                                </div>

                                <div class="location-crud__inline-actions">
                                    <button
                                        type="button"
                                        class="btn btn-sm btn-outline-primary"
                                        :class="{ active: pickOnMapEnabled }"
                                        :disabled="!hasEditorAccess"
                                        @click="togglePickOnMap"
                                    >
                                        {{ pickOnMapEnabled ? 'Carte active' : 'Positionner sur la carte' }}
                                    </button>
                                    <button
                                        v-if="canUseGeolocation"
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        :disabled="!canEditCurrentForm || isSaving"
                                        @click="useBrowserLocation"
                                    >
                                        Utiliser ma position
                                    </button>
                                    <button
                                        v-if="hasCoordinates"
                                        type="button"
                                        class="btn btn-sm btn-outline-secondary"
                                        @click="centerMapOnForm"
                                    >
                                        Centrer sur le point
                                    </button>
                                </div>

                                <div class="form-group">
                                    <div class="location-crud__range-head">
                                        <label for="location-radius" class="mb-0">Rayon de couverture</label>
                                        <strong>{{ form.radius }} m</strong>
                                    </div>
                                    <input
                                        id="location-radius"
                                        v-model.number="form.radius"
                                        type="range"
                                        min="50"
                                        max="2000"
                                        step="50"
                                        class="custom-range"
                                        :disabled="!canEditCurrentForm"
                                    />
                                </div>

                                <div class="form-group">
                                    <label class="d-block">Niveau de risque</label>
                                    <div class="location-crud__color-grid">
                                        <button
                                            v-for="option in colorOptions"
                                            :key="option.value"
                                            type="button"
                                            class="location-crud__color-option"
                                            :class="{ active: form.color === option.value }"
                                            :disabled="!canEditCurrentForm"
                                            @click="form.color = option.value"
                                        >
                                            <span
                                                class="location-crud__color-swatch"
                                                :style="{ backgroundColor: option.hex }"
                                            />
                                            <span>{{ option.text }}</span>
                                        </button>
                                    </div>
                                </div>

                                <div class="location-crud__footer">
                                    <button
                                        v-if="canEditCurrentForm"
                                        type="submit"
                                        class="btn btn-primary"
                                        :disabled="isSaving"
                                    >
                                        {{ isSaving ? 'Enregistrement...' : (selectedLocation ? $t('update') : $t('save')) }}
                                    </button>
                                    <button
                                        type="button"
                                        class="btn btn-secondary"
                                        @click="closeEditorDrawer"
                                    >
                                        Fermer
                                    </button>
                                    <button
                                        v-if="selectedLocation && canDelete"
                                        type="button"
                                        class="btn btn-outline-danger"
                                        @click="openDeleteModal"
                                    >
                                        Supprimer
                                    </button>
                                </div>
                            </form>
                        </aside>
                    </transition>
                </div>
            </section>
        </div>

        <transition name="location-delete-fade">
            <div
                v-if="deleteConfirmationModalActive"
                class="location-crud__confirm-layer"
                role="dialog"
                aria-modal="true"
            >
                <div class="location-crud__confirm-backdrop" @click="cancelled"></div>

                <div class="location-crud__confirm-card" @click.stop>
                    <div class="location-crud__confirm-icon">
                        <app-icon :name="'trash-2'"></app-icon>
                    </div>

                    <h5>Supprimer ce lieu ?</h5>
                    <p>
                        Cette action supprimera
                        <strong>{{ selectedLocation ? selectedLocation.name : 'ce lieu' }}</strong>
                        de manière définitive.
                    </p>

                    <div class="location-crud__confirm-actions">
                        <button type="button" class="btn btn-secondary" :disabled="deleteLoader" @click="cancelled">
                            Annuler
                        </button>
                        <button type="button" class="btn btn-danger" :disabled="deleteLoader" @click="confirmed">
                            {{ deleteLoader ? 'Suppression...' : 'Oui, supprimer' }}
                        </button>
                    </div>
                </div>
            </div>
        </transition>
    </div>
</template>

<script>
import axios from 'axios';
import * as actions from '../../../Config/ApiUrl';
import { getGoogleMapsErrorTranslationKey, loadGoogleMapsScript as loadGoogleMapsApiScript } from '../../../Helpers/GoogleMaps';
import CoreLibrary from '../../../../core/helpers/CoreLibrary';
import { mapState } from 'vuex';

const DEFAULT_RADIUS = 400;
const DEFAULT_CENTER = { lat: -11.6609, lng: 27.4794 };
const COORDINATE_PRECISION = 6;
const DEFAULT_LOCATION_PER_PAGE = 20;
const LOCATION_PER_PAGE_OPTIONS = [10, 20, 50, 100];

export default {
    extends: CoreLibrary,
    name: 'AppLocations',
    data() {
        return {
            mapBootTimeout: null,
            map: null,
            directionsService: null,
            draftMarker: null,
            draftCircle: null,
            roadRenderers: [],
            searchRefreshTimeout: null,
            locations: [],
            catalogLocations: [],
            incidents: [],
            locationPagination: this.defaultLocationPagination(),
            locationPerPageOptions: LOCATION_PER_PAGE_OPTIONS,
            selectedLocationId: null,
            isEditorDrawerOpen: false,
            isSaving: false,
            isLoadingLocations: false,
            deleteConfirmationModalActive: false,
            deleteLoader: false,
            reopenEditorAfterDeletePrompt: false,
            searchQuery: '',
            activePanel: 'search',
            pickOnMapEnabled: false,
            showIncidents: false,
            showRoads: false,
            roadDefinitions: [
                { origin: 'Kolwezi', destination: 'Kisamfu', color: '#0055ff' },
                { origin: 'Kisamfu', destination: 'Fungurume', color: '#f1c40f' },
                { origin: 'Fungurume', destination: 'Mutaka-Dilomba', color: '#0055ff' },
                { origin: 'Mutaka-Dilomba', destination: 'Sokoroshe', color: '#e11d48' },
                { origin: 'Sokoroshe', destination: 'Kasumbalesa', color: '#0055ff' },
                { origin: 'Kasumbalesa', destination: 'Mokambo', color: '#0055ff' },
                { origin: 'Mokambo', destination: 'Tonkoshi', color: '#e11d48' },
                { origin: 'Kasumbalesa', destination: 'Ndola', color: '#0055ff' },
                { origin: 'Tonkoshi', destination: 'Sakania', color: '#0055ff' },
                { origin: 'Sakania', destination: 'Ndola', color: '#0055ff' },
                { origin: 'Kasumbalesa', destination: 'Nakonde', color: '#0055ff' },
                { origin: 'Kasumbalesa', destination: 'Solwezi', color: '#0055ff' },
                { origin: 'Kasumbalesa', destination: 'Chirundu', color: '#0055ff' },
                { origin: 'Solwezi', destination: 'Katima Mulilo', color: '#0055ff' },
                { origin: 'Kasumbalesa', destination: 'Kazungula', color: '#0055ff' },
                { origin: 'Kazungula', destination: "Martin's drift", color: '#0055ff' },
                { origin: 'Ndola', destination: 'Kapiri', color: '#0055ff' },
                { origin: 'Kapiri', destination: 'Martin Drift', color: '#0055ff' },
            ],
            form: this.defaultForm(),
            colorOptions: [
                { value: 'blue', text: 'Bleu - Pas de risque', hex: '#2563eb' },
                { value: 'green', text: 'Vert - Risque faible', hex: '#16a34a' },
                { value: 'yellow', text: 'Jaune - Risque modéré', hex: '#eab308' },
                { value: 'orange', text: 'Orange - Risque élevé', hex: '#f97316' },
                { value: 'red', text: 'Rouge - Risque critique', hex: '#dc2626' },
                { value: 'black', text: 'Noir - Risque maximum', hex: '#111827' },
            ],
        };
    },
    computed: {
        ...mapState({
            locationTypeList: (state) => state.selects.locationTypeList,
        }),
        canCreate() {
            return this.$can('create_locations');
        },
        canUpdate() {
            return this.$can('update_locations');
        },
        canDelete() {
            return this.$can('delete_locations');
        },
        hasEditorAccess() {
            return this.canCreate || this.canUpdate;
        },
        selectedLocation() {
            return this.locations.find((location) => Number(location.id) === Number(this.selectedLocationId)) || null;
        },
        catalogTotal() {
            return Number(this.locationPagination.total) || this.locations.length;
        },
        firstFilteredLocation() {
            return this.filteredLocations.length ? this.filteredLocations[0] : null;
        },
        filteredLocations() {
            return this.catalogLocations;
        },
        showCatalogPagination() {
            return this.catalogTotal > 0;
        },
        hasCoordinates() {
            return this.parseCoordinate(this.form.lat) !== null && this.parseCoordinate(this.form.lng) !== null;
        },
        canEditCurrentForm() {
            if (this.selectedLocation) {
                return this.canUpdate;
            }

            return this.canCreate;
        },
        canUseGeolocation() {
            return typeof navigator !== 'undefined' && !!navigator.geolocation && this.canEditCurrentForm;
        },
        workspaceAssistantTitle() {
            if (this.pickOnMapEnabled) {
                return 'Positionnement en cours';
            }

            if (this.isEditorDrawerOpen) {
                return this.selectedLocation ? 'Configuration en cours' : 'Nouveau lieu';
            }

            if (this.searchQuery) {
                return 'Recherche active';
            }

            if (this.selectedLocation) {
                return `${this.selectedLocation.name}`;
            }

            return 'Navigation libre';
        },
        workspaceAssistantMessage() {
            if (this.pickOnMapEnabled) {
                return 'Cliquez sur la carte pour définir la position, puis ajustez les champs dans le panneau.';
            }

            if (this.isEditorDrawerOpen && this.selectedLocation) {
                return 'Modifiez les informations du lieu sélectionné puis enregistrez vos changements.';
            }

            if (this.isEditorDrawerOpen) {
                return 'Renseignez les informations du nouveau lieu ou utilisez la carte pour placer ses coordonnées.';
            }

            if (this.searchQuery) {
                return this.filteredLocations.length
                    ? 'Choisissez un résultat pour centrer la carte ou ouvrir sa configuration.'
                    : 'Aucun résultat pour cette recherche. Essayez un autre nom, type ou code.';
            }

            if (this.selectedLocation) {
                return 'Le lieu reste sélectionné sur la carte. Utilisez Configurer pour rouvrir le panneau.';
            }

            return 'Commencez par rechercher un lieu existant ou cliquez sur Ajouter pour en créer un nouveau.';
        },
        positioningHint() {
            if (this.pickOnMapEnabled) {
                return 'Cliquez sur la carte pour positionner le lieu, puis ajustez les détails dans le formulaire.';
            }

            if (this.hasCoordinates) {
                return 'Les coordonnées peuvent être modifiées à la main, depuis la carte ou via la géolocalisation du navigateur.';
            }

            return 'Commencez par le formulaire ou activez le positionnement sur carte pour créer un point rapidement.';
        },
        editorDescription() {
            if (this.selectedLocation) {
                return 'Vous pouvez modifier le lieu depuis la liste, la carte ou les champs manuels, sans perdre le contexte.';
            }

            return 'Le flux est hybride : saisissez les champs, placez le point sur la carte, ou combinez les deux.';
        },
    },
    watch: {
        'form.lat': 'syncDraftOverlay',
        'form.lng': 'syncDraftOverlay',
        'form.radius': 'syncDraftOverlay',
        'form.color': 'syncDraftOverlay',
        searchQuery() {
            this.scheduleSearchRefresh();
        },
        showIncidents() {
            this.applyIncidentVisibility();
        },
        showRoads(value) {
            if (!this.map) {
                return;
            }

            if (value) {
                this.ensureRoadOverlays();
                return;
            }

            this.clearRoadOverlays();
        },
    },
    mounted() {
        this.$store.dispatch('getLocationTypes');

        this.loadGoogleMapsScript()
            .then(() => {
                this.scheduleMapInitialization();
            })
            .catch((error) => {
                this.$toastr.e(this.resolveGoogleMapsErrorMessage(error));
            });
    },
    beforeDestroy() {
        if (this.mapBootTimeout) {
            window.clearTimeout(this.mapBootTimeout);
        }

        if (this.searchRefreshTimeout) {
            window.clearTimeout(this.searchRefreshTimeout);
        }

        this.clearLocationOverlays();
        this.clearIncidentOverlays();
        this.clearDraftOverlay();
        this.clearRoadOverlays();
    },
    methods: {
        defaultForm() {
            return {
                id: null,
                name: '',
                code: '',
                description: '',
                address: '',
                comment: '',
                color: 'blue',
                location_type_id: null,
                lat: '',
                lng: '',
                radius: DEFAULT_RADIUS,
            };
        },
        defaultLocationPagination() {
            return {
                currentPage: 1,
                lastPage: 1,
                perPage: DEFAULT_LOCATION_PER_PAGE,
                total: 0,
                from: 0,
                to: 0,
            };
        },
        loadGoogleMapsScript() {
            return loadGoogleMapsApiScript();
        },
        resolveGoogleMapsErrorMessage(error) {
            return this.$t(getGoogleMapsErrorTranslationKey(error));
        },
        scheduleMapInitialization(attempt = 0) {
            if (this.mapBootTimeout) {
                window.clearTimeout(this.mapBootTimeout);
            }

            this.$nextTick(() => {
                this.mapBootTimeout = window.setTimeout(() => {
                    const mapElement = this.$refs.map;

                    if (!mapElement) {
                        return;
                    }

                    if ((mapElement.offsetWidth === 0 || mapElement.offsetHeight === 0) && attempt < 8) {
                        this.scheduleMapInitialization(attempt + 1);
                        return;
                    }

                    this.initMap();
                    this.loadLocations({ fitBounds: true });
                    this.loadIncidents();
                }, attempt === 0 ? 80 : 140);
            });
        },
        initMap() {
            this.map = new google.maps.Map(this.$refs.map, {
                center: DEFAULT_CENTER,
                zoom: 9,
                mapTypeId: google.maps.MapTypeId.ROADMAP,
                fullscreenControl: false,
                streetViewControl: false,
                clickableIcons: false,
                gestureHandling: 'greedy',
                mapTypeControl: true,
                styles: [
                    {
                        featureType: 'poi.business',
                        stylers: [{ visibility: 'off' }],
                    },
                    {
                        featureType: 'transit',
                        stylers: [{ visibility: 'off' }],
                    },
                ],
            });

            this.directionsService = new google.maps.DirectionsService();

            this.map.addListener('click', (event) => {
                if (!this.pickOnMapEnabled || !this.canEditCurrentForm) {
                    return;
                }

                if (!this.selectedLocation && !this.canCreate) {
                    return;
                }

                if (!this.selectedLocation && this.canCreate) {
                    this.startCreateMode(false);
                }

                this.setDraftCoordinates(event.latLng.lat(), event.latLng.lng(), true);
                this.pickOnMapEnabled = false;
            });
        },
        async loadLocations(options = {}) {
            this.isLoadingLocations = true;

            try {
                const params = this.buildLocationParams(options);
                const response = await axios.get(actions.LOCATION, { params });
                const currentSelection = options.selection || (options.preserveSelection ? this.selectedLocationId : null);
                const requestedPage = Number(params.page) || 1;
                const items = this.applyLocationPagination(response.data, requestedPage);

                if (!items.length && this.locationPagination.total > 0 && requestedPage > 1) {
                    return await this.loadLocations({
                        ...options,
                        page: this.locationPagination.lastPage,
                        preserveSelection: false,
                    });
                }

                this.catalogLocations = items.map((location) => this.normalizeLocation(location));
                await this.loadMapLocations({
                    ...options,
                    selection: currentSelection,
                });

                if (options.resetScroll !== false) {
                    this.scrollCatalogToTop();
                }
            } catch (error) {
                this.$toastr.e(error?.response?.data?.message || 'Impossible de charger les lieux.');
            } finally {
                this.isLoadingLocations = false;
            }
        },
        async loadMapLocations(options = {}) {
            const params = this.buildMapLocationParams();
            const response = await axios.get(actions.LOCATION, { params });
            const currentSelection = options.selection || (options.preserveSelection ? this.selectedLocationId : null);

            this.clearLocationOverlays();
            this.locations = (Array.isArray(response.data) ? response.data : []).map((location) => this.normalizeLocation(location));

            if (!this.resolveSelection(currentSelection) && options.preserveSelection) {
                const preservedLocation = await this.fetchLocationById(this.getSelectionId(currentSelection));

                if (preservedLocation) {
                    this.locations = [
                        preservedLocation,
                        ...this.locations.filter((location) => Number(location.id) !== Number(preservedLocation.id)),
                    ];
                }
            }

            this.locations.forEach((location) => {
                this.addLocationMarker(location);
            });

            const matchedSelection = this.resolveSelection(currentSelection);

            if (matchedSelection) {
                this.selectLocation(matchedSelection, {
                    center: !!options.center,
                    openInfo: !!options.openInfo,
                });
            } else {
                this.selectedLocationId = null;
                this.applyLocationSelectionVisuals();

                if (!this.hasCoordinates) {
                    this.clearDraftOverlay();
                }
            }

            if (options.fitBounds) {
                this.fitMapToVisibleLocations();
            }
        },
        async loadIncidents() {
            try {
                const response = await axios.get('incidents');

                this.clearIncidentOverlays();
                this.incidents = (response.data?.data || []).map((incident) => ({
                    id: incident.id,
                    name: incident.name,
                    type: incident.type,
                    lat: this.parseCoordinate(incident.latitude),
                    lng: this.parseCoordinate(incident.longitude),
                })).filter((incident) => incident.lat !== null && incident.lng !== null);

                this.incidents.forEach((incident) => {
                    this.addIncidentMarker(incident);
                });

                this.applyIncidentVisibility();
            } catch (error) {
                this.$toastr.e(error?.response?.data?.message || 'Impossible de charger les incidents.');
            }
        },
        normalizeLocation(location) {
            const displayName = this.normalizeDisplayText(location.name, 'Sans nom');
            const displayTypeName = this.normalizeDisplayText(
                location.location_type?.name || location.locationType?.name,
                'Sans type'
            );

            return {
                id: location.id,
                name: displayName,
                code: location.code || '',
                description: location.description || '',
                address: location.address || '',
                comment: location.comment || '',
                color: location.color || 'blue',
                radius: Number(location.radius) || DEFAULT_RADIUS,
                lat: this.parseCoordinate(location.latitude ?? location.lat),
                lng: this.parseCoordinate(location.longitude ?? location.lng),
                location_type_id: location.location_type_id
                    ? Number(location.location_type_id)
                    : (location.locationType?.id ? Number(location.locationType.id) : null),
                locationTypeName: displayTypeName,
                displayName,
                displayTypeName,
                escorter: location.escorter || null,
                marker: null,
                circle: null,
                infoWindow: null,
            };
        },
        getCatalogLocationName(location) {
            return this.normalizeDisplayText(
                location?.displayName ?? location?.name,
                'Sans nom'
            );
        },
        getCatalogLocationTypeName(location) {
            return this.normalizeDisplayText(
                location?.displayTypeName
                ?? location?.locationTypeName
                ?? location?.location_type?.name
                ?? location?.locationType?.name,
                'Sans type'
            );
        },
        buildLocationParams(options = {}) {
            const page = Number(options.page || this.locationPagination.currentPage || 1);
            const perPage = Number(options.perPage || this.locationPagination.perPage || DEFAULT_LOCATION_PER_PAGE);
            const search = this.searchQuery.trim();

            return {
                paginate: 1,
                page,
                per_page: perPage,
                ...(search ? { search } : {}),
            };
        },
        buildMapLocationParams() {
            const search = this.searchQuery.trim();

            return {
                ...(search ? { search } : {}),
            };
        },
        applyLocationPagination(payload, fallbackPage = 1) {
            if (Array.isArray(payload)) {
                this.locationPagination = {
                    ...this.defaultLocationPagination(),
                    total: payload.length,
                    from: payload.length ? 1 : 0,
                    to: payload.length,
                    perPage: payload.length || DEFAULT_LOCATION_PER_PAGE,
                };

                return payload;
            }

            this.locationPagination = {
                currentPage: Number(payload?.current_page) || fallbackPage,
                lastPage: Number(payload?.last_page) || 1,
                perPage: Number(payload?.per_page) || DEFAULT_LOCATION_PER_PAGE,
                total: Number(payload?.total) || 0,
                from: Number(payload?.from) || 0,
                to: Number(payload?.to) || 0,
            };

            return Array.isArray(payload?.data) ? payload.data : [];
        },
        scheduleSearchRefresh() {
            if (this.searchRefreshTimeout) {
                window.clearTimeout(this.searchRefreshTimeout);
            }

            this.searchRefreshTimeout = window.setTimeout(() => {
                this.searchRefreshTimeout = null;
                this.loadLocations({
                    page: 1,
                    fitBounds: true,
                    preserveSelection: false,
                });
            }, 280);
        },
        async flushSearchRefresh(options = {}) {
            if (this.searchRefreshTimeout) {
                window.clearTimeout(this.searchRefreshTimeout);
                this.searchRefreshTimeout = null;
            }

            await this.loadLocations({
                page: 1,
                fitBounds: true,
                preserveSelection: false,
                ...options,
            });
        },
        changeLocationPage(page) {
            if (page < 1 || page > this.locationPagination.lastPage || page === this.locationPagination.currentPage) {
                return;
            }

            this.loadLocations({
                page,
                fitBounds: true,
                preserveSelection: false,
            });
        },
        changeLocationPerPage() {
            this.loadLocations({
                page: 1,
                fitBounds: true,
                perPage: this.locationPagination.perPage,
                preserveSelection: false,
            });
        },
        scrollCatalogToTop() {
            this.$nextTick(() => {
                const list = this.$refs.catalogList;

                if (!list) {
                    return;
                }

                if (typeof list.scrollTo === 'function') {
                    list.scrollTo({ top: 0, behavior: 'auto' });
                    return;
                }

                list.scrollTop = 0;
            });
        },
        getSelectionId(selection) {
            if (!selection) {
                return null;
            }

            if (typeof selection === 'number' || typeof selection === 'string') {
                return Number(selection) || selection;
            }

            if (selection.id) {
                return Number(selection.id) || selection.id;
            }

            return null;
        },
        async fetchLocationById(locationId) {
            if (!locationId) {
                return null;
            }

            try {
                const response = await axios.get(`${actions.LOCATION}/${locationId}`);

                return this.normalizeLocation(response.data);
            } catch (error) {
                return null;
            }
        },
        clearLocationOverlays() {
            this.locations.forEach((location) => {
                if (location.infoWindow) {
                    location.infoWindow.close();
                }

                if (location.marker) {
                    location.marker.setMap(null);
                }

                if (location.circle) {
                    location.circle.setMap(null);
                }
            });
        },
        clearIncidentOverlays() {
            this.incidents.forEach((incident) => {
                if (incident.marker) {
                    incident.marker.setMap(null);
                }

                if (incident.circle) {
                    incident.circle.setMap(null);
                }
            });
        },
        addLocationMarker(location) {
            if (!this.map || !Number.isFinite(location.lat) || !Number.isFinite(location.lng)) {
                return;
            }

            const position = { lat: location.lat, lng: location.lng };
            const marker = new google.maps.Marker({
                position,
                map: this.map,
                title: location.name,
                icon: {
                    url: this.getIconUrlByType(location.locationTypeName),
                    scaledSize: new google.maps.Size(36, 36),
                    anchor: new google.maps.Point(18, 18),
                },
            });

            const circle = new google.maps.Circle({
                map: this.map,
                center: position,
                radius: Number(location.radius) || DEFAULT_RADIUS,
                fillColor: this.resolveColorHex(location.color),
                fillOpacity: 0.16,
                strokeColor: this.resolveColorHex(location.color),
                strokeWeight: 1.5,
            });

            const infoWindow = new google.maps.InfoWindow({
                content: this.buildInfoWindowContent(location),
            });

            marker.addListener('click', () => {
                this.selectLocation(location, { center: true, openInfo: true });
            });

            location.marker = marker;
            location.circle = circle;
            location.infoWindow = infoWindow;
        },
        addIncidentMarker(incident) {
            if (!this.map) {
                return;
            }

            const position = { lat: incident.lat, lng: incident.lng };
            const marker = new google.maps.Marker({
                position,
                map: this.showIncidents ? this.map : null,
                title: incident.name,
                icon: {
                    url: '/icons/pin.png',
                    scaledSize: new google.maps.Size(34, 34),
                    anchor: new google.maps.Point(17, 34),
                },
                opacity: 0.9,
            });

            const circle = new google.maps.Circle({
                map: this.showIncidents ? this.map : null,
                center: position,
                radius: 250,
                fillColor: '#dc2626',
                fillOpacity: 0.14,
                strokeColor: '#dc2626',
                strokeWeight: 1.2,
            });

            incident.marker = marker;
            incident.circle = circle;
        },
        applyIncidentVisibility() {
            this.incidents.forEach((incident) => {
                if (incident.marker) {
                    incident.marker.setMap(this.showIncidents ? this.map : null);
                }

                if (incident.circle) {
                    incident.circle.setMap(this.showIncidents ? this.map : null);
                }
            });
        },
        ensureRoadOverlays() {
            if (!this.directionsService || !this.map) {
                return;
            }

            if (this.roadRenderers.length) {
                this.roadRenderers.forEach((renderer) => renderer.setMap(this.map));
                return;
            }

            this.roadDefinitions.forEach((road) => {
                const renderer = new google.maps.DirectionsRenderer({
                    suppressMarkers: true,
                    preserveViewport: true,
                    polylineOptions: {
                        strokeColor: road.color,
                        strokeOpacity: 0.65,
                        strokeWeight: 5,
                    },
                });

                this.directionsService.route({
                    origin: road.origin,
                    destination: road.destination,
                    travelMode: google.maps.TravelMode.DRIVING,
                }, (result, status) => {
                    if (status !== google.maps.DirectionsStatus.OK) {
                        renderer.setMap(null);
                        return;
                    }

                    renderer.setDirections(result);
                    renderer.setMap(this.map);
                });

                this.roadRenderers.push(renderer);
            });
        },
        clearRoadOverlays() {
            this.roadRenderers.forEach((renderer) => renderer.setMap(null));
        },
        buildInfoWindowContent(location) {
            const escorterBlock = location.escorter
                ? `
                    <div style="margin-top: 10px; color: #475569;">
                        Escorte: ${this.escapeHtml([
                            location.escorter.first_name,
                            location.escorter.middle_name,
                            location.escorter.last_name,
                        ].filter(Boolean).join(' '))}
                    </div>
                `
                : '';

            return `
                <div style="min-width: 220px; padding: 8px 4px; color: #0f172a;">
                    <strong style="display:block; margin-bottom: 4px;">${this.escapeHtml(location.name)}</strong>
                    <span style="display:block; margin-bottom: 8px; color: #64748b;">${this.escapeHtml(location.locationTypeName)}</span>
                    <div style="font-size: 12px; line-height: 1.6;">
                        <div>Latitude: ${this.formatCoordinate(location.lat)}</div>
                        <div>Longitude: ${this.formatCoordinate(location.lng)}</div>
                        <div>Rayon: ${Number(location.radius) || DEFAULT_RADIUS} m</div>
                    </div>
                    ${escorterBlock}
                </div>
            `;
        },
        selectLocation(location, options = {}) {
            const resolvedLocation = this.resolveSelection(location) || location;

            if (!resolvedLocation) {
                return;
            }

            const selectedLocation = resolvedLocation;

            this.selectedLocationId = selectedLocation.id;
            this.pickOnMapEnabled = false;
            this.form = {
                id: selectedLocation.id,
                name: selectedLocation.name || '',
                code: selectedLocation.code || '',
                description: selectedLocation.description || '',
                address: selectedLocation.address || '',
                comment: selectedLocation.comment || '',
                color: selectedLocation.color || 'blue',
                location_type_id: selectedLocation.location_type_id || null,
                lat: this.formatCoordinateForInput(selectedLocation.lat),
                lng: this.formatCoordinateForInput(selectedLocation.lng),
                radius: Number(selectedLocation.radius) || DEFAULT_RADIUS,
            };

            this.syncDraftOverlay();
            this.applyLocationSelectionVisuals();

            if (options.openDrawer !== false) {
                this.openEditorDrawer('configure');
            }

            if (options.center) {
                this.focusMapOnCoordinates(selectedLocation.lat, selectedLocation.lng, 14);
            }

            if (options.openInfo && selectedLocation.infoWindow && selectedLocation.marker) {
                this.closeInfoWindows();
                selectedLocation.infoWindow.open(this.map, selectedLocation.marker);
            }
        },
        closeInfoWindows() {
            this.locations.forEach((location) => {
                if (location.infoWindow) {
                    location.infoWindow.close();
                }
            });
        },
        applyLocationSelectionVisuals() {
            this.locations.forEach((location) => {
                const isActive = Number(location.id) === Number(this.selectedLocationId);

                if (location.marker) {
                    location.marker.setOpacity(isActive ? 1 : 0.92);
                    location.marker.setZIndex(isActive ? 200 : 10);
                }

                if (location.circle) {
                    location.circle.setOptions({
                        fillOpacity: isActive ? 0.22 : 0.12,
                        strokeWeight: isActive ? 2.2 : 1.2,
                    });
                }
            });
        },
        startCreateMode(enableMapPick = true) {
            if (!this.canCreate) {
                return;
            }

            this.selectedLocationId = null;
            this.closeInfoWindows();
            this.form = this.defaultForm();
            this.pickOnMapEnabled = enableMapPick;
            this.syncDraftOverlay();
            this.applyLocationSelectionVisuals();
            this.openEditorDrawer('add');
        },
        openEditorDrawer(panel = 'configure') {
            this.activePanel = panel;
            this.isEditorDrawerOpen = true;
            this.$nextTick(() => {
                if (this.$refs.nameInput) {
                    this.$refs.nameInput.focus();
                }
            });
        },
        closeEditorDrawer() {
            this.isEditorDrawerOpen = false;
            this.pickOnMapEnabled = false;
            this.activePanel = 'search';
        },
        activateAddMode() {
            if (!this.canCreate) {
                this.$toastr.e('Vous n\'avez pas les permissions pour ajouter un lieu.');
                return;
            }

            this.startCreateMode(false);
        },
        activateSearchMode() {
            this.closeEditorDrawer();
            this.$nextTick(() => {
                if (this.$refs.searchInput) {
                    this.$refs.searchInput.focus();
                }
            });
        },
        activateConfigureMode() {
            if (this.selectedLocation) {
                this.openEditorDrawer('configure');
                return;
            }

            if (this.firstFilteredLocation) {
                this.configureLocation(this.firstFilteredLocation);
                return;
            }

            this.$toastr.e('Aucun lieu disponible à configurer.');
        },
        async clearSearch() {
            this.searchQuery = '';
            this.activateSearchMode();
            await this.flushSearchRefresh();
        },
        openDeleteModal() {
            if (!this.selectedLocation || !this.canDelete) {
                return;
            }

            this.reopenEditorAfterDeletePrompt = this.isEditorDrawerOpen;
            this.isEditorDrawerOpen = false;
            this.pickOnMapEnabled = false;

            this.$nextTick(() => {
                this.deleteConfirmationModalActive = true;
            });
        },
        async openFirstSearchResult() {
            if (this.searchRefreshTimeout) {
                await this.flushSearchRefresh();
            }

            if (!this.firstFilteredLocation) {
                this.$toastr.e('Aucun résultat pour cette recherche.');
                return;
            }

            this.configureLocation(this.firstFilteredLocation);
        },
        focusLocation(location) {
            this.activePanel = 'search';
            this.selectLocation(location, { center: true, openInfo: true, openDrawer: false });
        },
        configureLocation(location) {
            this.selectLocation(location, { center: true, openInfo: true });
        },
        togglePickOnMap() {
            if (!this.hasEditorAccess) {
                this.$toastr.e('Vous n\'avez pas les permissions pour positionner un lieu.');
                return;
            }

            if (!this.canEditCurrentForm && !(this.canCreate && !this.selectedLocation)) {
                return;
            }

            if (!this.selectedLocation && !this.hasCoordinates && this.canCreate) {
                this.startCreateMode(false);
            }

            this.openEditorDrawer(this.selectedLocation ? 'configure' : 'add');
            this.pickOnMapEnabled = !this.pickOnMapEnabled;
        },
        useBrowserLocation() {
            if (!this.canUseGeolocation) {
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => {
                    if (!this.selectedLocation && this.canCreate) {
                        this.startCreateMode(false);
                    }

                    this.setDraftCoordinates(position.coords.latitude, position.coords.longitude, true);
                    this.pickOnMapEnabled = false;
                },
                (error) => {
                    this.$toastr.e(error.message || 'Impossible de récupérer la position du navigateur.');
                },
                {
                    enableHighAccuracy: true,
                    timeout: 10000,
                }
            );
        },
        setDraftCoordinates(lat, lng, centerMap = false) {
            this.form.lat = this.formatCoordinateForInput(lat);
            this.form.lng = this.formatCoordinateForInput(lng);

            if (!this.form.radius) {
                this.form.radius = DEFAULT_RADIUS;
            }

            this.syncDraftOverlay();

            if (centerMap) {
                this.focusMapOnCoordinates(lat, lng, 15);
            }
        },
        syncDraftOverlay() {
            if (!this.map || !window.google || !window.google.maps) {
                return;
            }

            const lat = this.parseCoordinate(this.form.lat);
            const lng = this.parseCoordinate(this.form.lng);

            if (lat === null || lng === null) {
                this.clearDraftOverlay();
                return;
            }

            const position = { lat, lng };
            const radius = Number(this.form.radius) || DEFAULT_RADIUS;
            const fillColor = this.resolveColorHex(this.form.color);

            if (!this.draftMarker) {
                this.draftMarker = new google.maps.Marker({
                    position,
                    map: this.map,
                    draggable: this.canEditCurrentForm,
                    zIndex: 400,
                    icon: {
                        path: google.maps.SymbolPath.CIRCLE,
                        fillColor: '#0f766e',
                        fillOpacity: 1,
                        strokeColor: '#ffffff',
                        strokeWeight: 2,
                        scale: 8,
                    },
                });

                this.draftMarker.addListener('dragend', (event) => {
                    this.form.lat = this.formatCoordinate(event.latLng.lat());
                    this.form.lng = this.formatCoordinate(event.latLng.lng());
                    this.pickOnMapEnabled = false;
                });
            }

            if (!this.draftCircle) {
                this.draftCircle = new google.maps.Circle({
                    map: this.map,
                    center: position,
                    radius,
                    fillColor,
                    fillOpacity: 0.12,
                    strokeColor: fillColor,
                    strokeWeight: 2,
                    strokeOpacity: 0.9,
                    zIndex: 350,
                });
            }

            this.draftMarker.setMap(this.map);
            this.draftMarker.setDraggable(this.canEditCurrentForm);
            this.draftMarker.setPosition(position);

            this.draftCircle.setMap(this.map);
            this.draftCircle.setCenter(position);
            this.draftCircle.setRadius(radius);
            this.draftCircle.setOptions({
                fillColor,
                strokeColor: fillColor,
            });
        },
        clearDraftOverlay() {
            if (this.draftMarker) {
                this.draftMarker.setMap(null);
            }

            if (this.draftCircle) {
                this.draftCircle.setMap(null);
            }
        },
        resetEditor() {
            this.pickOnMapEnabled = false;

            if (this.selectedLocation) {
                this.selectLocation(this.selectedLocation);
                return;
            }

            this.form = this.defaultForm();
            this.clearDraftOverlay();
            this.openEditorDrawer('add');
        },
        centerMapOnForm() {
            if (!this.hasCoordinates) {
                return;
            }

            this.focusMapOnCoordinates(this.parseCoordinate(this.form.lat), this.parseCoordinate(this.form.lng), 15);
        },
        centerMapOnActiveTarget() {
            if (this.hasCoordinates) {
                this.centerMapOnForm();
                return;
            }

            if (this.selectedLocation) {
                this.focusMapOnCoordinates(this.selectedLocation.lat, this.selectedLocation.lng, 14);
            }
        },
        fitMapToVisibleLocations() {
            if (!this.map || !this.locations.length) {
                return;
            }

            if (this.locations.length === 1) {
                this.focusMapOnCoordinates(this.locations[0].lat, this.locations[0].lng, 14);
                return;
            }

            const bounds = new google.maps.LatLngBounds();
            this.locations.forEach((location) => {
                if (Number.isFinite(location.lat) && Number.isFinite(location.lng)) {
                    bounds.extend({ lat: Number(location.lat), lng: Number(location.lng) });
                }
            });

            this.map.fitBounds(bounds);
        },
        focusMapOnCoordinates(lat, lng, zoom = 14) {
            if (!this.map || lat === null || lng === null) {
                return;
            }

            this.map.setCenter({ lat: Number(lat), lng: Number(lng) });
            this.map.setZoom(zoom);
        },
        resolveSelection(selection) {
            if (!selection) {
                return null;
            }

            if (typeof selection === 'number' || typeof selection === 'string') {
                return this.locations.find((location) => Number(location.id) === Number(selection)) || null;
            }

            if (selection.id) {
                return this.locations.find((location) => Number(location.id) === Number(selection.id)) || null;
            }

            const latitude = this.parseCoordinate(selection.latitude ?? selection.lat);
            const longitude = this.parseCoordinate(selection.longitude ?? selection.lng);

            return this.locations.find((location) => {
                if (selection.name && location.name !== selection.name) {
                    return false;
                }

                if (selection.location_type_id && Number(location.location_type_id) !== Number(selection.location_type_id)) {
                    return false;
                }

                return this.isSameCoordinate(location.lat, latitude) && this.isSameCoordinate(location.lng, longitude);
            }) || null;
        },
        isSameCoordinate(first, second) {
            if (first === null || second === null || first === undefined || second === undefined) {
                return false;
            }

            return Math.abs(Number(first) - Number(second)) <= 0.00001;
        },
        validateForm() {
            if (!this.canEditCurrentForm) {
                return 'Vous n\'avez pas les permissions pour modifier ce lieu.';
            }

            if (!this.form.name) {
                return 'Le nom est obligatoire.';
            }

            if (!this.form.location_type_id) {
                return 'Le type de lieu est obligatoire.';
            }

            const latitude = this.parseCoordinate(this.form.lat);
            const longitude = this.parseCoordinate(this.form.lng);

            if (latitude === null || latitude < -90 || latitude > 90) {
                return 'La latitude est invalide.';
            }

            if (longitude === null || longitude < -180 || longitude > 180) {
                return 'La longitude est invalide.';
            }

            return null;
        },
        async saveLocation() {
            const errorMessage = this.validateForm();

            if (errorMessage) {
                this.$toastr.e(errorMessage);
                return;
            }

            const latitude = this.parseCoordinate(this.form.lat);
            const longitude = this.parseCoordinate(this.form.lng);

            const payload = {
                name: this.form.name,
                code: this.form.code || '',
                description: this.form.description || '',
                address: this.form.address || '',
                comment: this.form.comment || '',
                color: this.form.color || 'blue',
                radius: Number(this.form.radius) || DEFAULT_RADIUS,
                latitude,
                longitude,
                location_type_id: Number(this.form.location_type_id),
            };

            this.isSaving = true;

            try {
                let response;
                let selection = this.selectedLocationId;

                if (this.selectedLocationId) {
                    response = await axios.patch(`${actions.LOCATION}/${this.selectedLocationId}`, payload);
                } else {
                    response = await axios.post(actions.LOCATION, payload);
                }

                selection = response?.data?.location?.id || selection;

                this.$toastr.s(response?.data?.message || 'Lieu enregistré avec succès.');
                this.pickOnMapEnabled = false;
                this.openEditorDrawer('configure');

                await this.loadLocations({
                    selection,
                    center: true,
                    openInfo: true,
                    preserveSelection: true,
                });

                this.$store.dispatch('getLocations');
            } catch (error) {
                this.$toastr.e(error?.response?.data?.message || 'Impossible d\'enregistrer le lieu.');
            } finally {
                this.isSaving = false;
            }
        },
        async deleteLocation() {
            if (!this.selectedLocation) {
                return;
            }

            await axios.delete(`${actions.LOCATION}/${this.selectedLocation.id}`);
            this.$store.dispatch('getLocations');
        },
        async confirmed() {
            if (!this.selectedLocation) {
                this.cancelled();
                return;
            }

            this.deleteLoader = true;

            try {
                await this.deleteLocation();
                this.$toastr.s('Lieu supprimé avec succès.');
                this.selectedLocationId = null;
                this.reopenEditorAfterDeletePrompt = false;
                this.closeEditorDrawer();
                this.form = this.defaultForm();
                this.clearDraftOverlay();
                this.closeInfoWindows();
                await this.loadLocations({
                    fitBounds: true,
                    page: this.filteredLocations.length === 1 && this.locationPagination.currentPage > 1
                        ? this.locationPagination.currentPage - 1
                        : this.locationPagination.currentPage,
                });
            } catch (error) {
                this.$toastr.e(error?.response?.data?.message || 'Impossible de supprimer ce lieu.');
            } finally {
                this.deleteLoader = false;
                this.cancelled();
            }
        },
        cancelled() {
            this.deleteConfirmationModalActive = false;

            if (this.reopenEditorAfterDeletePrompt && this.selectedLocation) {
                this.reopenEditorAfterDeletePrompt = false;
                this.openEditorDrawer('configure');
                return;
            }

            this.reopenEditorAfterDeletePrompt = false;
        },
        parseCoordinate(value) {
            if (value === '' || value === null || value === undefined) {
                return null;
            }

            const parsed = Number(String(value).trim().replace(/,/g, '.'));
            return Number.isFinite(parsed) ? parsed : null;
        },
        formatCoordinateForInput(value) {
            const parsed = this.parseCoordinate(value);

            return parsed === null ? '' : parsed.toFixed(COORDINATE_PRECISION);
        },
        formatCoordinate(value) {
            const parsed = this.parseCoordinate(value);
            return parsed === null ? '---' : parsed.toFixed(COORDINATE_PRECISION);
        },
        formatCoordinates(lat, lng) {
            if (!Number.isFinite(Number(lat)) || !Number.isFinite(Number(lng))) {
                return 'Coordonnées indisponibles';
            }

            return `${this.formatCoordinate(lat)}, ${this.formatCoordinate(lng)}`;
        },
        formatRadius(value) {
            return `${Number(value) || DEFAULT_RADIUS} m`;
        },
        getIconUrlByType(type) {
            const basePath = '/icons/';
            const typeMap = {
                'Check Point': `${basePath}check-point.png`,
                'Toll Point / Péage': `${basePath}toll-point.png`,
                Parking: `${basePath}parking.png`,
                Village: `${basePath}village.png`,
                'City / Ville': `${basePath}city.png`,
                'Border / Frontière': `${basePath}border.png`,
                Site: `${basePath}site.png`,
                ShortCut: `${basePath}shortcut.png`,
                'Railway Station': `${basePath}railway-station.png`,
            };

            return typeMap[type] || `${basePath}default-icon.png`;
        },
        resolveColorHex(color) {
            const option = this.colorOptions.find((item) => item.value === color);
            return option ? option.hex : '#2563eb';
        },
        normalizeDisplayText(value, fallback) {
            const normalizedValue = `${value ?? ''}`.trim();

            return normalizedValue ? normalizedValue : fallback;
        },
        escapeHtml(value) {
            return String(value || '')
                .replace(/&/g, '&amp;')
                .replace(/</g, '&lt;')
                .replace(/>/g, '&gt;')
                .replace(/"/g, '&quot;')
                .replace(/'/g, '&#39;');
        },
    },
};
</script>

<style>
.location-crud {
    --location-bg: linear-gradient(180deg, #f7fafc 0%, #edf2f7 100%);
    --location-panel: rgba(255, 255, 255, 0.94);
    --location-border: rgba(148, 163, 184, 0.24);
    --location-shadow: 0 24px 60px rgba(15, 23, 42, 0.08);
    --location-text: #0f172a;
    --location-muted: #64748b;
    --location-accent: #0f766e;
    --location-accent-soft: rgba(15, 118, 110, 0.08);
    --location-danger: #dc2626;
}

.location-crud__header-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
    flex-wrap: wrap;
}

.location-crud__header-actions .btn.active {
    box-shadow: 0 0 0 0.2rem rgba(15, 118, 110, 0.12);
}

.location-crud__workspace {
    display: grid;
    grid-template-columns: minmax(320px, 420px) minmax(0, 1fr);
    gap: 1.25rem;
    align-items: start;
}

.location-crud__sidebar {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    min-width: 0;
}

.location-crud__panel {
    background: var(--location-panel);
    border: 1px solid var(--location-border);
    border-radius: 1.25rem;
    box-shadow: var(--location-shadow);
    backdrop-filter: blur(14px);
}

.location-crud__panel--active {
    border-color: rgba(15, 118, 110, 0.32);
    box-shadow: 0 24px 60px rgba(15, 118, 110, 0.12);
}

.location-crud__panel--search {
    padding: 1.25rem;
}

.location-crud__panel--list {
    padding: 1.1rem;
    min-height: 22rem;
    max-height: min(38rem, calc(100vh - 17rem));
    display: flex;
    flex-direction: column;
    overflow: hidden;
}

.location-crud__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 1rem;
}

.location-crud__panel-head small {
    display: inline-block;
    color: var(--location-accent);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 0.3rem;
}

.location-crud__panel-head h4 {
    margin: 0;
    color: var(--location-text);
    font-size: 1.15rem;
}

.location-crud__panel-head p {
    margin: 0.4rem 0 0;
    color: var(--location-muted);
    font-size: 0.92rem;
    line-height: 1.5;
}

.location-crud__count {
    padding: 0.45rem 0.75rem;
    background: var(--location-accent-soft);
    border-radius: 999px;
    color: var(--location-accent);
    font-size: 0.86rem;
    font-weight: 700;
}

.location-crud__search {
    margin-bottom: 1rem;
}

.location-crud__search .form-control {
    min-height: 3rem;
    border-radius: 0.95rem;
    border: 1px solid rgba(148, 163, 184, 0.32);
    box-shadow: none;
}

.location-crud__search .btn {
    min-width: 5.4rem;
}

.location-crud__stats {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.location-crud__stat-card {
    padding: 0.9rem 0.95rem;
    background: linear-gradient(180deg, rgba(248, 250, 252, 0.95) 0%, rgba(241, 245, 249, 0.95) 100%);
    border: 1px solid rgba(148, 163, 184, 0.18);
    border-radius: 1rem;
}

.location-crud__stat-label {
    display: block;
    margin-bottom: 0.35rem;
    color: var(--location-muted);
    font-size: 0.76rem;
    text-transform: uppercase;
    letter-spacing: 0.06em;
}

.location-crud__stat-card strong {
    color: var(--location-text);
    font-size: 1.15rem;
}

.location-crud__assistant-note {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    margin-bottom: 1rem;
    padding: 0.95rem 1rem;
    border-radius: 1rem;
    background: rgba(255, 255, 255, 0.88);
    border: 1px solid rgba(148, 163, 184, 0.18);
}

.location-crud__assistant-note strong {
    color: var(--location-text);
    font-size: 0.95rem;
}

.location-crud__assistant-note span {
    color: var(--location-muted);
    font-size: 0.88rem;
    line-height: 1.5;
}

.location-crud__toggles {
    display: grid;
    gap: 0.75rem;
}

.location-crud__toggle {
    display: flex;
    align-items: center;
    gap: 0.65rem;
    margin: 0;
    padding: 0.85rem 1rem;
    border-radius: 0.95rem;
    background: rgba(248, 250, 252, 0.8);
    border: 1px solid rgba(148, 163, 184, 0.18);
    color: var(--location-text);
    cursor: pointer;
}

.location-crud__toggle input {
    accent-color: var(--location-accent);
}

.location-crud__list {
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    gap: 0.9rem;
    min-height: 0;
    overflow-y: auto;
    padding-right: 0.35rem;
    padding-bottom: 0.2rem;
}

.location-crud__list-item {
    border: 1px solid rgba(148, 163, 184, 0.2);
    border-radius: 1rem;
    background: white;
    color: var(--location-text);
    transition: transform 0.18s ease, border-color 0.18s ease, box-shadow 0.18s ease;
    overflow: hidden;
}

.location-crud__list-item:hover {
    transform: translateY(-1px);
    border-color: rgba(15, 118, 110, 0.35);
    box-shadow: 0 16px 24px rgba(15, 23, 42, 0.08);
}

.location-crud__list-item.active {
    border-color: rgba(15, 118, 110, 0.48);
    background: rgba(240, 253, 250, 0.9);
    box-shadow: 0 18px 30px rgba(15, 118, 110, 0.12);
}

.location-crud__list-card {
    position: relative;
    display: block;
    width: 100%;
    min-height: 4.75rem;
    border: 0;
    border-radius: 0;
    appearance: none;
    -webkit-appearance: none;
    background: transparent;
    box-shadow: none;
    color: inherit;
    cursor: pointer;
    font: inherit;
    text-align: left;
    padding: 1.05rem 1.1rem 0.8rem;
}

.location-crud__list-card:focus {
    outline: none;
}

.location-crud__list-main {
    display: grid;
    gap: 0.28rem;
    width: 100%;
    min-width: 0;
    margin-bottom: 0.2rem;
}

.location-crud__list-title {
    display: block;
    color: var(--location-text);
    font-size: 1rem;
    line-height: 1.35;
    font-weight: 700;
    letter-spacing: 0;
    opacity: 1;
    visibility: visible;
    white-space: normal;
    word-break: break-word;
}

.location-crud__list-subtitle,
.location-crud__list-meta span {
    display: block;
    color: var(--location-muted);
    font-size: 0.85rem;
    line-height: 1.45;
    opacity: 1;
    visibility: visible;
    white-space: normal;
    word-break: break-word;
}

.location-crud__list-meta {
    display: grid;
    gap: 0.45rem;
}

.location-crud__list-meta span {
    display: flex;
    flex-wrap: wrap;
    gap: 0.35rem;
    align-items: baseline;
    overflow-wrap: anywhere;
}

.location-crud__list-meta strong {
    color: var(--location-text);
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.03em;
    text-transform: uppercase;
}

.location-crud__list-row-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.55rem;
    padding: 0 1.1rem 1.05rem;
}

.location-crud__pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.9rem;
    flex-wrap: wrap;
    margin-top: 0.95rem;
    padding-top: 0.9rem;
    border-top: 1px solid rgba(148, 163, 184, 0.18);
}

.location-crud__pagination-summary {
    display: inline-flex;
    align-items: baseline;
    gap: 0.35rem;
    color: var(--location-muted);
    font-size: 0.86rem;
}

.location-crud__pagination-summary strong,
.location-crud__pagination-status {
    color: var(--location-text);
    font-weight: 700;
}

.location-crud__pagination-controls {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.65rem;
    flex-wrap: wrap;
    min-width: 0;
}

.location-crud__per-page {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    margin: 0;
    color: var(--location-muted);
    font-size: 0.84rem;
    white-space: nowrap;
}

.location-crud__per-page select {
    min-width: 4.8rem;
    border-radius: 0.8rem;
    flex-shrink: 0;
}

.location-crud__pagination-nav {
    display: inline-flex;
    align-items: center;
    gap: 0.55rem;
    flex-wrap: nowrap;
    flex-shrink: 0;
}

.location-crud__pagination-icon {
    width: 2.25rem;
    height: 2.25rem;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.8rem;
    flex-shrink: 0;
}

.location-crud__pagination-status {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 4.9rem;
    min-height: 2.25rem;
    padding: 0.35rem 0.8rem;
    border-radius: 999px;
    border: 1px solid rgba(148, 163, 184, 0.22);
    background: rgba(248, 250, 252, 0.95);
    white-space: nowrap;
}

.location-crud__empty {
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 12rem;
    color: var(--location-muted);
    font-size: 0.95rem;
    text-align: center;
    padding: 1rem;
}

.location-crud__hint {
    margin-bottom: 1rem;
    padding: 0.9rem 1rem;
    border-radius: 1rem;
    background: rgba(248, 250, 252, 0.92);
    border: 1px solid rgba(148, 163, 184, 0.18);
    color: var(--location-muted);
    font-size: 0.9rem;
    line-height: 1.5;
}

.location-crud__hint--active {
    background: rgba(15, 118, 110, 0.08);
    border-color: rgba(15, 118, 110, 0.28);
    color: var(--location-accent);
}

.location-crud__form {
    display: flex;
    flex-direction: column;
    gap: 0.15rem;
}

.location-crud__form .form-group label {
    color: var(--location-text);
    font-weight: 600;
}

.location-crud__form .form-control,
.location-crud__form .custom-range {
    border-radius: 0.95rem;
}

.location-crud__form textarea.form-control {
    min-height: 5.2rem;
}

.location-crud__grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 0.9rem;
}

.location-crud__inline-actions {
    display: flex;
    gap: 0.6rem;
    flex-wrap: wrap;
    margin-bottom: 1rem;
}

.location-crud__inline-actions .btn.active {
    background: rgba(15, 118, 110, 0.1);
    border-color: rgba(15, 118, 110, 0.45);
    color: var(--location-accent);
}

.location-crud__range-head {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.5rem;
    color: var(--location-text);
}

.location-crud__color-grid {
    display: grid;
    gap: 0.65rem;
}

.location-crud__color-option {
    display: flex;
    align-items: center;
    gap: 0.7rem;
    width: 100%;
    text-align: left;
    padding: 0.8rem 0.95rem;
    border-radius: 1rem;
    border: 1px solid rgba(148, 163, 184, 0.2);
    background: white;
    color: var(--location-text);
    transition: border-color 0.18s ease, box-shadow 0.18s ease;
}

.location-crud__color-option.active {
    border-color: rgba(15, 118, 110, 0.4);
    box-shadow: 0 14px 24px rgba(15, 118, 110, 0.08);
}

.location-crud__color-swatch {
    width: 0.95rem;
    height: 0.95rem;
    border-radius: 999px;
    border: 2px solid rgba(255, 255, 255, 0.9);
    box-shadow: 0 0 0 1px rgba(15, 23, 42, 0.12);
    flex-shrink: 0;
}

.location-crud__footer {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 0.5rem;
}

.location-crud__map-section {
    background: var(--location-bg);
    border-radius: 1.5rem;
    border: 1px solid rgba(148, 163, 184, 0.22);
    padding: 1rem;
    min-height: calc(100vh - 12rem);
    box-shadow: var(--location-shadow);
    position: relative;
    overflow: hidden;
}

.location-crud__map-canvas {
    position: relative;
}

.location-crud__map-section--drawer-open .location-crud__map-toolbar {
    padding-right: min(36rem, 42vw);
}

.location-crud__map-toolbar {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.9rem;
}

.location-crud__legend,
.location-crud__map-actions {
    display: flex;
    gap: 0.55rem;
    flex-wrap: wrap;
}

.location-crud__chip {
    display: inline-flex;
    align-items: center;
    padding: 0.45rem 0.8rem;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.86);
    border: 1px solid rgba(148, 163, 184, 0.2);
    color: var(--location-text);
    font-size: 0.84rem;
    font-weight: 600;
}

.location-crud__chip--filled {
    background: rgba(15, 118, 110, 0.12);
    border-color: rgba(15, 118, 110, 0.28);
    color: var(--location-accent);
}

.location-crud__map-notice {
    margin-bottom: 0.8rem;
    padding: 0.85rem 1rem;
    border-radius: 1rem;
    background: rgba(15, 118, 110, 0.09);
    border: 1px solid rgba(15, 118, 110, 0.22);
    color: var(--location-accent);
    font-size: 0.9rem;
}

.location-crud__drawer {
    position: absolute;
    top: 1rem;
    right: 1rem;
    bottom: 1rem;
    width: min(35rem, 42vw);
    display: flex;
    flex-direction: column;
    gap: 0.9rem;
    padding: 1.1rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.96);
    border: 1px solid rgba(148, 163, 184, 0.24);
    box-shadow: 0 30px 60px rgba(15, 23, 42, 0.18);
    backdrop-filter: blur(14px);
    z-index: 3;
    overflow-y: auto;
}

.location-crud__drawer--active {
    border-color: rgba(15, 118, 110, 0.3);
}

.location-crud__drawer-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
}

.location-crud__drawer-header small {
    display: inline-block;
    color: var(--location-accent);
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 0.3rem;
}

.location-crud__drawer-header h4 {
    margin: 0;
    color: var(--location-text);
    font-size: 1.15rem;
}

.location-crud__drawer-header p {
    margin: 0.4rem 0 0;
    color: var(--location-muted);
    font-size: 0.92rem;
    line-height: 1.5;
}

.location-crud__drawer-header-actions {
    display: flex;
    align-items: center;
    gap: 0.55rem;
}

.location-crud__drawer-form {
    flex: 1 1 auto;
}

.location-editor-drawer-enter-active,
.location-editor-drawer-leave-active {
    transition: transform 220ms ease, opacity 220ms ease;
}

.location-editor-drawer-enter,
.location-editor-drawer-leave-to {
    transform: translate3d(1.5rem, 0, 0);
    opacity: 0;
}

.location-crud__confirm-layer {
    position: fixed;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
    z-index: 1400;
}

.location-crud__confirm-backdrop {
    position: absolute;
    inset: 0;
    background: rgba(7, 16, 27, 0.64);
    backdrop-filter: blur(4px);
}

.location-crud__confirm-card {
    position: relative;
    width: min(28rem, 100%);
    padding: 1.6rem;
    border-radius: 1.35rem;
    background: rgba(255, 255, 255, 0.98);
    box-shadow: 0 32px 70px rgba(15, 23, 42, 0.28);
    border: 1px solid rgba(148, 163, 184, 0.22);
    text-align: center;
}

.location-crud__confirm-icon {
    width: 3.5rem;
    height: 3.5rem;
    margin: 0 auto 1rem;
    border-radius: 999px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: rgba(220, 38, 38, 0.12);
    color: #dc2626;
}

.location-crud__confirm-card h5 {
    margin-bottom: 0.65rem;
    color: var(--location-text);
}

.location-crud__confirm-card p {
    margin: 0;
    color: var(--location-muted);
    line-height: 1.6;
}

.location-crud__confirm-actions {
    display: flex;
    justify-content: center;
    gap: 0.75rem;
    flex-wrap: wrap;
    margin-top: 1.4rem;
}

.location-delete-fade-enter-active,
.location-delete-fade-leave-active {
    transition: opacity 180ms ease;
}

.location-delete-fade-enter,
.location-delete-fade-leave-to {
    opacity: 0;
}

#map {
    width: 100%;
    height: calc(100vh - 18rem);
    min-height: 40rem;
    border-radius: 1.25rem;
    overflow: hidden;
    box-shadow: inset 0 0 0 1px rgba(148, 163, 184, 0.18);
}

@media (max-width: 1199px) {
    .location-crud__workspace {
        grid-template-columns: 1fr;
    }

    .location-crud__sidebar {
        max-height: none;
    }

    .location-crud__panel--list {
        max-height: none;
    }

    .location-crud__map-section--drawer-open .location-crud__map-toolbar {
        padding-right: 0;
    }

    .location-crud__drawer {
        position: relative;
        inset: auto;
        width: 100%;
        margin-top: 1rem;
        max-height: none;
    }

    #map,
    .location-crud__map-section {
        min-height: 32rem;
    }
}

@media (max-width: 767px) {
    .location-crud__stats,
    .location-crud__grid {
        grid-template-columns: 1fr;
    }

    .location-crud__panel-head,
    .location-crud__map-toolbar,
    .location-crud__drawer-header {
        flex-direction: column;
    }

    .location-crud__header-actions {
        justify-content: flex-start;
    }

    .location-crud__list-row-actions {
        justify-content: stretch;
    }

    .location-crud__list-row-actions .btn {
        flex: 1 1 0;
    }

    .location-crud__pagination,
    .location-crud__pagination-controls {
        align-items: stretch;
    }

    #map {
        height: 28rem;
        min-height: 28rem;
    }
}
</style>
